import { useState } from 'react';
import type { AssignmentForLearner, AssignmentResource, SessionDetail } from '@lp/shared';
import { CheckCircle2, Circle, Clock, Download, ExternalLink, Eye } from 'lucide-react';
import { Alert, Badge, Button, Card, CardHeader, SectionHeading, clsx } from '../ui.js';
import { ConfirmDialog } from '../Modal.js';
import { Markdown } from '../Markdown.js';
import { formatBytes } from '../../lib/format.js';
import { useChecklistToggle } from '../../lib/useChecklist.js';
import { useReveal } from '../../lib/useReveal.js';

/**
 * The take-home assignment.
 *
 * There is no submission anywhere in this product and the copy says so
 * plainly. The only thing it persists is the learner's own done tick, and the
 * reference solution sits behind a confirmation because the value of a
 * take-home is gone the moment somebody clicks past it by accident.
 */
export function AssignmentSection({
  assignment,
  sessionQueryKey,
}: {
  assignment: AssignmentForLearner;
  sessionQueryKey: readonly unknown[];
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const toggle = useChecklistToggle<SessionDetail>(sessionQueryKey, (previous, _itemId, done) =>
    previous.assignment
      ? { ...previous, assignment: { ...previous.assignment, done } }
      : previous,
  );

  const reveal = useReveal(sessionQueryKey);

  const starters = assignment.resources.filter((r) => r.kind === 'file');
  const links = assignment.resources.filter((r) => r.kind === 'link');

  return (
    <section aria-labelledby="session-assignment">
      <SectionHeading id="session-assignment">Assignment</SectionHeading>

      <Card>
        <CardHeader
          title={assignment.title}
          description="Ungraded practice. Nothing is submitted and nobody reviews it — the only record is the tick you keep for yourself."
          actions={
            assignment.estimatedEffort ? (
              <Badge icon={<Clock size={12} aria-hidden />}>{assignment.estimatedEffort}</Badge>
            ) : null
          }
        />

        <div className="p-5 flex flex-col gap-5">
          <Markdown>{assignment.promptMd}</Markdown>

          {assignment.suggestedApproachMd ? (
            <div className="rounded-control border border-line bg-surface-2/60 p-4">
              <h3 className="text-sm font-semibold text-ink mb-2">A suggested approach</h3>
              <Markdown compact>{assignment.suggestedApproachMd}</Markdown>
            </div>
          ) : null}

          {assignment.resources.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-ink mb-2">Resources</h3>
              <ul className="flex flex-col gap-2">
                {starters.map((resource) => (
                  <li key={resource.id}>
                    <ResourceRow resource={resource} />
                  </li>
                ))}
                {links.map((resource) => (
                  <li key={resource.id}>
                    <ResourceRow resource={resource} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="border-t border-line pt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={assignment.done}
              onClick={() =>
                toggle.mutate({
                  itemType: 'assignment',
                  itemId: assignment.id,
                  done: !assignment.done,
                })
              }
              className={clsx(
                'inline-flex items-center gap-2 rounded-control border px-3.5 py-2 text-sm font-medium transition',
                assignment.done
                  ? 'bg-accent-wash border-accent/40 text-ink'
                  : 'bg-surface border-line-strong text-ink hover:bg-surface-2',
              )}
            >
              {assignment.done ? (
                <CheckCircle2 size={16} className="text-ok" aria-hidden />
              ) : (
                <Circle size={16} className="text-ink-subtle" aria-hidden />
              )}
              {assignment.done ? 'Marked as done' : 'Mark as done'}
            </button>
            <p className="text-xs text-ink-subtle">
              Just for you — it moves nothing and tells nobody.
            </p>
          </div>

          {assignment.hasReferenceSolution ? (
            <div className="border-t border-line pt-4">
              <h3 className="text-sm font-semibold text-ink">Reference solution</h3>
              {assignment.solutionRevealed && assignment.referenceSolutionMd ? (
                <div className="mt-2 rounded-control border border-line bg-surface-2/60 p-4">
                  <Markdown>{assignment.referenceSolutionMd}</Markdown>
                </div>
              ) : (
                <>
                  <p className="text-sm text-ink-muted mt-1 leading-relaxed max-w-prose">
                    One way the author would solve this. It stays closed until you ask for it,
                    because seeing it first is the one thing that makes this exercise pointless.
                  </p>
                  <Button
                    className="mt-3"
                    variant="secondary"
                    icon={<Eye size={15} aria-hidden />}
                    loading={reveal.isPending}
                    onClick={() => setConfirmOpen(true)}
                  >
                    Show the reference solution
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </div>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Show the reference solution?"
        description="This cannot be un-seen, and it stays open from now on."
        confirmLabel="Show it"
        details={
          <Alert tone="warn" title="Have a real attempt first">
            Even a rough, half-finished attempt makes the reference solution far more useful than
            reading it cold. Nothing is graded either way.
          </Alert>
        }
        onConfirm={async () => {
          await reveal.mutateAsync({
            targetType: 'assignment_solution',
            targetId: assignment.id,
            confirm: true,
          });
          setConfirmOpen(false);
        }}
      />
    </section>
  );
}

export function ResourceRow({
  resource,
}: {
  resource: AssignmentResource & { isStarter?: boolean };
}) {
  const isFile = resource.kind === 'file';
  const href = isFile ? resource.downloadUrl : resource.url;
  const name = resource.fileName ?? resource.title;

  const body = (
    <>
      <span
        aria-hidden
        className="grid place-items-center h-9 w-9 shrink-0 rounded-control bg-surface-2 border border-line text-ink-muted"
      >
        {isFile ? <Download size={16} /> : <ExternalLink size={16} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-medium text-ink truncate">{resource.title}</span>
          {resource.isStarter ? <Badge tone="project">Starter</Badge> : null}
        </span>
        {resource.description ? (
          <span className="block text-xs text-ink-muted mt-0.5 leading-relaxed">
            {resource.description}
          </span>
        ) : null}
        <span className="block text-xs text-ink-subtle truncate mt-0.5">
          {isFile ? name : resource.url}
          {isFile && resource.fileSize ? ` · ${formatBytes(resource.fileSize)}` : ''}
        </span>
      </span>
    </>
  );

  if (!href) {
    return (
      <div className="flex items-start gap-3 rounded-control border border-line px-3 py-2.5 opacity-70">
        {body}
      </div>
    );
  }

  return (
    <a
      href={href}
      {...(isFile
        ? { download: name }
        : { target: '_blank', rel: 'noopener noreferrer' })}
      className="flex items-start gap-3 rounded-control border border-line px-3 py-2.5 hover:bg-surface-2 hover:border-line-strong transition"
    >
      {body}
      {!isFile ? <span className="sr-only">(opens in a new tab)</span> : null}
    </a>
  );
}
